import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Rocket, CheckCircle, Clock, Users, TrendingUp, Calendar } from 'lucide-react';

export const ImplementationPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center space-x-4 mb-8">
        <button
          onClick={() => navigate('/')}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors"
          title="Powrót do głównej"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-3xl font-bold gradient-text mb-2">Wdróż u siebie</h1>
          <p className="text-gray-600 dark:text-gray-300">
            Przenieś CallOS do swojej organizacji
          </p>
        </div>
      </div>

      <div className="space-y-8">
        {/* Hero Section */}
        <div className="glass-card rounded-2xl p-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl blur-lg opacity-30 animate-pulse-glow"></div>
              <div className="relative bg-gradient-to-r from-primary-500 to-secondary-500 p-4 rounded-xl">
                <Rocket className="w-12 h-12 text-white" />
              </div>
            </div>
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            Właśnie zobaczyłeś CallOS w akcji!
          </h2>
          
          <p className="text-lg text-gray-700 dark:text-gray-300 mb-6">
            Przeanalizowałeś swoją rozmowę i widziałeś, jak system automatycznie:
          </p>
          
          <div className="grid md:grid-cols-3 gap-4 mb-8">
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
              <div className="text-blue-600 dark:text-blue-400 mb-2">📝</div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Transkrypcja i podsumowanie</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Stworzył automatyczną transkrypcję i podsumowanie</p>
            </div>
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-xl">
              <div className="text-green-600 dark:text-green-400 mb-2">📊</div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Ocena jakości</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Ocenił jakość rozmowy sprzedażowej</p>
            </div>
            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl">
              <div className="text-purple-600 dark:text-purple-400 mb-2">💡</div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Konkretny feedback</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Dostarczył feedback dopasowany do etapu sprzedaży</p>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-primary-100 to-secondary-100 dark:from-primary-900/30 dark:to-secondary-900/30 rounded-xl p-6">
            <p className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Wyobraź sobie, że to dzieje się po KAŻDEJ rozmowie w Twoim zespole
            </p>
            <p className="text-gray-700 dark:text-gray-300">
              – automatycznie, bez dodatkowej pracy.
            </p>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="glass-card rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6 text-center">
            To, co właśnie przetestowałeś, może działać w Twojej firmie non-stop:
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="bg-green-100 dark:bg-green-900/30 p-3 rounded-xl">
                  <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  CRM wypełnia się sam
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  po każdej rozmowie
                </p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-xl">
                  <Users className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  Każdy sprzedawca dostaje feedback
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  natychmiast po rozmowie
                </p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="bg-purple-100 dark:bg-purple-900/30 p-3 rounded-xl">
                  <TrendingUp className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  Ty masz pełny przegląd jakości
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  bez godzin odsłuchiwania
                </p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="bg-orange-100 dark:bg-orange-900/30 p-3 rounded-xl">
                  <Rocket className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  Zespół się rozwija
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  dzięki konsekwentnemu coachingowi
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Implementation Process */}
        <div className="glass-card rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6 text-center">
            Jak wygląda wdrożenie?
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  <div className="bg-primary-100 dark:bg-primary-900/30 p-3 rounded-xl">
                    <Clock className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                    10 dni
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    tyle zajmuje uruchomienie podstawy
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  <div className="bg-secondary-100 dark:bg-secondary-900/30 p-3 rounded-xl">
                    <CheckCircle className="w-6 h-6 text-secondary-600 dark:text-secondary-400" />
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                    Integracja z Twoim CRM
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    wypełnianie pól, aktualizacje statusów
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-6">
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  <div className="bg-accent-100 dark:bg-accent-900/30 p-3 rounded-xl">
                    <Users className="w-6 h-6 text-accent-600 dark:text-accent-400" />
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                    Dostosowanie do Twojego procesu
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    feedback zgodny z Twoimi standardami
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  <div className="bg-green-100 dark:bg-green-900/30 p-3 rounded-xl">
                    <TrendingUp className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                    Zero zmiany nawyków
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    CallOS działa w tle, zespół pracuje jak zwykle
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="glass-card rounded-2xl p-8 text-center bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-primary-900/20 dark:to-secondary-900/20">
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl blur-lg opacity-30 animate-pulse-glow"></div>
              <div className="relative bg-gradient-to-r from-primary-500 to-secondary-500 p-4 rounded-xl">
                <Calendar className="w-12 h-12 text-white" />
              </div>
            </div>
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            Gotowy przenieść to do swojej firmy?
          </h2>
          
          <p className="text-lg text-gray-700 dark:text-gray-300 mb-6">
            Porozmawiajmy o Twoim wdrożeniu
          </p>
          
          <p className="text-gray-600 dark:text-gray-400 mb-8">
            Umów się na bezpłatną 30 minutową konsultację i porozmawiajmy o tym, co możemy zrobić dla Twojej firmy.
          </p>
          
          <button
            onClick={() => window.open('https://sailes.tech/kontakt/', '_blank')}
            className="inline-flex items-center space-x-2 px-8 py-4 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white font-semibold rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg"
          >
            <Calendar className="w-5 h-5" />
            <span>Umów konsultację</span>
          </button>
        </div>
      </div>
    </div>
  );
};